import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../api/client';
import { useNotify } from '../../../components/notifications';
import type { PreLoginSchedule } from './types';

/**
 * Loads a Pre-Login Setting's task schedules.
 *
 * <p>Lives outside the dialog because the FORM needs the count while the dialog
 * is CLOSED: "#Days In Advance" is gated on whether a schedule exists, and a
 * count that only arrived once the user opened the popup would leave the field
 * wrongly unlocked (or wrongly locked) until they did. One fetch, one source of
 * truth, shared by the gate and the popup's Refresh.</p>
 */
export function usePreLoginSchedules(cocOid: string | null) {
  const notify = useNotify();

  const [rows, setRows]       = useState<PreLoginSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  /** -1 = not loaded yet. The gate must not fire on "0 so far". */
  const [count, setCount]     = useState(-1);

  const load = useCallback(async () => {
    if (!cocOid) { setRows([]); setCount(-1); return; }
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/pre-login-settings/${cocOid}/schedules`);
      const list: PreLoginSchedule[] = res.data?.data ?? [];
      setRows(list);
      setCount(list.length);
    } catch {
      notify.error('Could not load the task schedule.');
    } finally {
      setLoading(false);
    }
  }, [cocOid, notify]);

  useEffect(() => { void load(); }, [load]);

  return { rows, count, loading, reload: load };
}
